export const MODEL_TABS = [
  { value: 'first', label: 'First touch' },
  { value: 'last', label: 'Last touch' },
  { value: 'linear', label: 'Multi-touch' },
];

export const DATE_MODE_TABS = [
  { value: 'order_date', label: 'Order date' },
  { value: 'event_date', label: 'Event date' },
];

export const METRIC_TABS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'tickets', label: 'Tickets' },
];

export const GROUP_TABS = [
  { value: 'channel', label: 'Channel' },
  { value: 'source', label: 'Source' },
  { value: 'partner', label: 'Partner' },
];

export const chartCaption = (metric) => (metric === 'revenue'
  ? 'Solid bars are credited revenue; faded bars are influenced revenue.'
  : 'Credited tickets per row.');

export const WINDOW_PRESETS = [1, 7, 14, 30];
export const CUSTOM_WINDOW = 'custom';

export const PRODUCT_LABELS = { classes: 'Classes', resale: 'Resale' };

const DAY_MS = 24 * 60 * 60 * 1000;

const countFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const formatCents = (cents) => moneyFormatter.format((cents || 0) / 100);
export const formatCount = (value) => countFormatter.format(value || 0);
export const formatRoas = (roas) => (roas == null ? '—' : `${roas.toFixed(2)}x`);
export const formatCoverage = (pct) => (pct == null ? '—' : `${pct}%`);
export const formatTrackingDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const windowOptions = (defaultWindowDays) => {
  const days = WINDOW_PRESETS.includes(defaultWindowDays) ? WINDOW_PRESETS : [...WINDOW_PRESETS, defaultWindowDays].sort((a, b) => a - b);
  return [
    ...days.map((day) => ({ value: String(day), label: day === 1 ? '1 day' : `${day} days` })),
    { value: CUSTOM_WINDOW, label: 'Custom' },
  ];
};

export const rangeDays = ({ start, end } = {}) => {
  if (!start || !end) return null;
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / DAY_MS) + 1;
};

export const rangeErrorFor = (dateRange) => {
  const days = rangeDays(dateRange);
  if (days == null) return 'Choose a start and end date.';
  if (days < 1) return 'The start date must be on or before the end date.';
  return null;
};

const present = (values) => values && values.length > 0;

export const buildReportParams = ({
  dateRange, model, windowDays, dateMode, includeRenewals, filters = {}, calendarEventIds,
}) => {
  const params = {
    start_date: dateRange?.start,
    end_date: dateRange?.end,
    model,
    window_days: windowDays,
    date_mode: dateMode,
    include_renewals: includeRenewals,
  };
  if (present(filters.venue_ownership_id)) params.venue_ownership_id = filters.venue_ownership_id;
  if (present(filters.product_type)) params.product_type = filters.product_type;
  const eventIds = present(calendarEventIds) ? calendarEventIds : filters.calendar_event_id;
  if (present(eventIds)) params.calendar_event_id = eventIds;
  return params;
};

export const toQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) value.forEach((item) => query.append(`${key}[]`, item));
    else query.append(key, value);
  });
  return query.toString();
};

export const tableRows = (rows, expandedKeys) => rows.flatMap((row) => {
  const parent = { ...row, rowKey: row.key, depth: 0, expandable: row.children.length > 0 };
  if (!expandedKeys[row.key]) return [parent];
  return [parent, ...row.children.map((child) => ({ ...child, rowKey: `${row.key}/${child.key}`, depth: 1, expandable: false }))];
});

export const CHART_BAR_LIMIT = 10;
export const OTHER_BAR_KEY = 'other';

const chartBar = (row, metric) => ({
  key: row.key,
  label: row.label,
  credited: metric === 'tickets' ? row.tickets : row.revenue_cents / 100,
  influenced: metric === 'tickets' ? 0 : row.influenced_revenue_cents / 100,
});

export const chartRows = (rows, metric, limit = CHART_BAR_LIMIT) => {
  const bars = rows.map((row) => chartBar(row, metric))
    .sort((a, b) => (b.credited - a.credited) || (b.influenced - a.influenced));
  if (bars.length <= limit) return bars;
  const rest = bars.slice(limit - 1);
  return [
    ...bars.slice(0, limit - 1),
    {
      key: OTHER_BAR_KEY,
      label: `Other (${rest.length})`,
      credited: rest.reduce((sum, bar) => sum + bar.credited, 0),
      influenced: rest.reduce((sum, bar) => sum + bar.influenced, 0),
    },
  ];
};

export const truncateLabel = (label, max = 20) => (label.length > max ? `${label.slice(0, max - 1)}…` : label);

export const windowDraftError = (value, maxWindowDays) => {
  const days = Number(value);
  if (value === '' || !Number.isInteger(days) || days < 1 || days > maxWindowDays) return `Enter 1–${maxWindowDays} days`;
  return null;
};
